import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, Download, MessageCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getProduct } from "../api";
import { toast } from "../hooks/use-toast";
import { company } from "../mock";

const waNumber = (company.whatsapp || "").replace(/[^0-9]/g, "");
const waLink = (name) =>
  `https://wa.me/${waNumber}?text=${encodeURIComponent(
    `Hi Bhawani Kitchen Equipment, I'm interested in the "${name}". Please share details, price and availability.`
  )}`;

const FEATURES = [
  "Energy-efficient compressor for lower running costs",
  "Uniform cooling with forced-air circulation",
  "Durable powder-coated / stainless steel body",
  "Low-maintenance design with easy servicing",
  "Optimised for Indian climate & voltage conditions",
];

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    getProduct(id)
      .then(setProduct)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div>
      <Header />
      <div className="pt-28 pb-20 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Link to="/#products" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[color:var(--brand-blue)] transition-colors mb-8">
            <ArrowLeft size={16} /> Back to products
          </Link>

          {loading && <p className="text-slate-400 py-20 text-center">Loading product...</p>}

          {notFound && !loading && (
            <div className="py-20 text-center">
              <p className="text-slate-500 mb-4">Sorry, this product could not be found.</p>
              <Link to="/#products" className="brand-btn inline-flex px-6 py-3 rounded-md font-semibold">Browse products</Link>
            </div>
          )}

          {product && (
            <>
              <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-start">
                <div className="relative">
                  <div className="absolute -inset-3 rounded-3xl -z-10" style={{ background: "var(--brand-ice)" }} />
                  <img src={product.image} alt={product.name} className="rounded-2xl object-contain bg-white w-full h-[340px] md:h-[460px] shadow-lg p-4" />
                </div>

                <div>
                  <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-4" style={{ background: "var(--brand-ice)", color: "var(--brand-blue)" }}>
                    {product.category}
                  </span>
                  <h1 className="section-title text-3xl md:text-4xl mb-4">{product.name}</h1>
                  <p className="text-slate-600 leading-relaxed mb-8">{product.desc}</p>

                  <div className="flex flex-wrap gap-4">
                    <a href={waLink(product.name)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md font-semibold text-white bg-[#25D366] hover:bg-[#1ebe57] transition-colors">
                      <MessageCircle size={18} /> Enquire on WhatsApp
                    </a>
                    <Link to="/#contact" className="brand-btn inline-flex items-center gap-2 px-6 py-3.5 rounded-md font-semibold">
                      Request a Quote
                    </Link>
                    {product.brochure ? (
                      <a
                        href={product.brochure}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        data-testid="product-brochure-download"
                        className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md font-semibold border border-slate-300 text-slate-700 hover:border-[color:var(--brand-blue)] hover:text-[color:var(--brand-blue)] transition-colors"
                      >
                        <Download size={18} /> Download Brochure
                      </a>
                    ) : (
                      <button
                        onClick={() => toast({ title: "Brochure", description: "A downloadable brochure will be attached soon." })}
                        className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md font-semibold border border-slate-300 text-slate-700 hover:border-[color:var(--brand-blue)] hover:text-[color:var(--brand-blue)] transition-colors"
                      >
                        <Download size={18} /> Download Brochure
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-16 grid md:grid-cols-2 gap-10">
                <div>
                  <h2 className="font-head font-bold text-2xl mb-5 text-[color:var(--brand-navy)]">Key Features</h2>
                  <ul className="space-y-3">
                    {FEATURES.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-slate-600">
                        <span className="h-6 w-6 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: "var(--brand-ice)" }}>
                          <Check size={14} className="text-[color:var(--brand-blue)]" />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-2xl p-8" style={{ background: "var(--brand-ice)" }}>
                  <h3 className="font-head font-bold text-xl mb-3 text-[color:var(--brand-navy)]">Need a custom configuration?</h3>
                  <p className="text-slate-600 mb-6">Our team can tailor capacity, finish and cooling to match your kitchen or retail space. Talk to us for a personalised recommendation.</p>
                  <Link to="/#contact" className="brand-btn inline-flex items-center gap-2 px-6 py-3.5 rounded-md font-semibold">
                    Talk to our experts
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
